import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WaButton from '../../components/wa/WaButton';
import { subscribeToBillingProfile, isPendingVerification } from '../../services/billingService';
import { isStudentPrincipal } from '../../services/principalStudents';
import { Card } from './customerUi';

/* Dashboard nudge: tuition is owed and there is no payment method on file.
 *
 * WHY (2026-09-21). Two new families in one week could not find where to add
 * a payment method. The control lives at More → Payments → Add a payment
 * method, two taps past the dashboard behind a tab called "More". Both
 * families were in exactly this state — an open balance and nothing on file —
 * which is when a parent needs the control most and when the dashboard said
 * nothing about it.
 *
 * WHEN IT SHOWS, and only then: balanceCents > 0 AND no paymentMethod AND
 * autopay is not sitting in ACH pending_verification (a family whose bank is
 * on microdeposits has done their part; nagging them is wrong) AND the family
 * is not on an external rail. A balance WITH a method on file is the autopay
 * run's job, not the parent's, so that state renders nothing.
 *
 * THE EXTERNAL-RAIL EXCLUSION CAME FROM THE FIRST READ OF REAL DATA (2026-09-21,
 * before this shipped): of the two families then owed-with-nothing-on-file,
 * one was Reece, who pays by Achieving Dreams grant checks
 * (`chargePolicy: 'external'`, core.md → Tuition). Nothing in the balance or
 * the method state says so; the banner would have told a grant-funded family
 * to add a card. Same lesson as registerStep1Nudge: every selection rule for
 * a family-facing message is wrong the first time in a way only real data
 * shows, so read the collection before flipping anything on.
 *
 * A student principal never sees it — Payments is a guardian-only route (P2b)
 * and a child should not be told to pay tuition.
 *
 * SAME READ AS THE PAYMENTS PAGE, not a second shape: subscribeToBillingProfile
 * is the one family-grained billing read the rules grant a guardian
 * (familyId == myFamilyId()), and it is live so the nudge disappears the
 * moment the webhook records a saved method. A family that has never started
 * setup has no billingProfiles doc at all — null here means nothing is owed,
 * which is an empty state and correctly renders nothing.
 *
 * The button is the same deep link the 09-21 login fix made reliable
 * (postLoginPath.js): a parent who reaches it signed out lands on Payments
 * after sign-in rather than back here. */

export function shouldNudge(profile) {
	if (!profile) return false;
	const owed = Number(profile.balanceCents) > 0;
	if (profile.chargePolicy === 'external') return false;
	return owed && !profile.paymentMethod && !isPendingVerification(profile);
}

const PaymentMethodNudge = ({ familyId, userProfile }) => {
	const navigate = useNavigate();
	const [profile, setProfile] = useState(null);
	const guardian = Boolean(familyId) && !isStudentPrincipal(userProfile);

	useEffect(() => {
		if (!guardian) return undefined;
		return subscribeToBillingProfile(familyId, setProfile, () => setProfile(null));
	}, [guardian, familyId]);

	if (!guardian || !shouldNudge(profile)) return null;

	return (
		<Card
			style={{
				marginBottom: 14,
				padding: '14px 16px',
				display: 'flex',
				alignItems: 'center',
				gap: 14,
				flexWrap: 'wrap',
				borderColor: 'color-mix(in srgb, var(--cyv-date, #ed7749) 45%, var(--cyv-line, #e3ddd4))',
				background: 'color-mix(in srgb, var(--cyv-date, #ed7749) 7%, var(--surface-card, #fff))',
			}}>
			<span
				style={{
					width: 40,
					height: 40,
					borderRadius: 12,
					flex: '0 0 auto',
					background: 'color-mix(in srgb, var(--cyv-date, #ed7749) 14%, var(--surface-card, #fff))',
					color: 'var(--cyv-date, #d9776a)',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}>
				<i className='fa-sharp fa-solid fa-credit-card' aria-hidden='true' />
			</span>
			<div style={{ flex: '1 1 200px', minWidth: 0 }}>
				<div
					style={{
						fontWeight: 'var(--fw-semibold, 600)',
						color: 'var(--text-heading, #384971)',
						lineHeight: 1.3,
					}}>
					Tuition is due and there's no payment method on file yet.
				</div>
				<div style={{ fontSize: '0.86rem', color: 'var(--text-muted, #7b8499)', marginTop: 3 }}>
					Bank account or card, no processing fees. Adding one doesn't charge anything by itself.
				</div>
			</div>
			<WaButton variant='brand' onClick={() => navigate('/portal/customer/payments')}>
				<i slot='start' className='fa-sharp fa-solid fa-credit-card'></i>
				Add a payment method
			</WaButton>
		</Card>
	);
};

export default PaymentMethodNudge;
